/**
 * Smooth virtual scroll with IntersectionObserver
 */

import { RAFLoop, lerp, clamp, createObserver, prefersReducedMotion } from '../core/utils.js';

export class SmoothScroll {
  #container;
  #content;
  #current = 0;
  #target = 0;
  #maxScroll = 0;
  #ease = 0.08;
  #rafLoop;
  #progressBar;
  #scrollIndicator;
  #resizeObserver;
  #onScrollCallbacks = new Set();
  #abortController = new AbortController();

  constructor() {
    this.#container = document.getElementById('scrollContainer');
    this.#content = document.getElementById('scrollContent');
    this.#progressBar = document.getElementById('progressBar');
    this.#scrollIndicator = document.getElementById('scrollIndicator');

    if (!this.#container || !this.#content) {
      console.warn('[SmoothScroll] Required elements not found');
      return;
    }

    // Disable smooth scroll for reduced motion
    if (prefersReducedMotion()) {
      this.#container.style.overflow = 'auto';
      this.#content.style.position = 'relative';
      return;
    }

    this.#init();
  }

  #init() {
    const signal = this.#abortController.signal;

    // Wheel handling
    window.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.#updateMaxScroll();
      this.#target = clamp(this.#target + e.deltaY, 0, this.#maxScroll);
    }, { passive: false, signal });

    // Touch handling
    let touchStartY = 0;
    let lastTouchY = 0;

    window.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
      lastTouchY = touchStartY;
    }, { passive: true, signal });

    window.addEventListener('touchmove', (e) => {
      const currentY = e.touches[0].clientY;
      const delta = lastTouchY - currentY;
      lastTouchY = currentY;
      
      this.#updateMaxScroll();
      this.#target = clamp(this.#target + delta * 2, 0, this.#maxScroll);
    }, { passive: true, signal });

    // Anchor links (Event Delegation)
    document.body.addEventListener('click', (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (anchor) {
        e.preventDefault();
        const targetId = anchor.getAttribute('href');
        
        if (targetId === '#') {
          this.scrollTo(0);
          return;
        }
        
        // If we are on a dynamic page (home-sections is hidden), route to home first
        const homeSections = document.getElementById('home-sections');
        if (homeSections && homeSections.style.display === 'none') {
          window.history.pushState({}, '', '/' + targetId);
          window.dispatchEvent(new Event('popstate'));
          
          setTimeout(() => {
            const target = document.querySelector(targetId);
            if (target) this.scrollTo(target.offsetTop);
          }, 100);
          return;
        }

        const target = document.querySelector(targetId);
        if (target) {
          this.scrollTo(target.offsetTop);
        }
      }
    }, { signal });

    // Resize observer for dynamic content
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#updateMaxScroll();
      });
      this.#resizeObserver.observe(this.#content);
    } else {
      window.addEventListener('resize', () => this.#updateMaxScroll(), { passive: true, signal });
    }

    // RAF loop
    this.#rafLoop = new RAFLoop(() => this.#tick(), { fps: 60 });
    this.#rafLoop.start();

    // Initial calculation
    this.#updateMaxScroll();
  }

  #tick() {
    // Smooth interpolation
    this.#current = lerp(this.#current, this.#target, this.#ease);

    // Apply transform
    this.#content.style.transform = `translateY(${-this.#current}px)`;

    // Update progress bar
    if (this.#progressBar && this.#maxScroll > 0) {
      const progress = (this.#current / this.#maxScroll) * 100;
      this.#progressBar.style.width = `${progress}%`;
      this.#progressBar.setAttribute('aria-valuenow', Math.round(progress));
    }

    // Hide scroll indicator after scrolling
    if (this.#scrollIndicator) {
      this.#scrollIndicator.classList.toggle('hidden', this.#current > 100);
    }

    // Notify subscribers
    this.#onScrollCallbacks.forEach(cb => cb(this.#current, this.#maxScroll));
  }

  #updateMaxScroll() {
    this.#maxScroll = Math.max(0, this.#content.scrollHeight - window.innerHeight);
    this.#target = clamp(this.#target, 0, this.#maxScroll);
  }

  // Public API
  scrollTo(y) {
    this.#target = clamp(y, 0, this.#maxScroll);
  }

  getScrollY() {
    return this.#current;
  }

  onScroll(callback) {
    this.#onScrollCallbacks.add(callback);
    return () => this.#onScrollCallbacks.delete(callback);
  }

  destroy() {
    this.#abortController.abort();
    this.#rafLoop?.stop();
    this.#resizeObserver?.disconnect();
    this.#onScrollCallbacks.clear();
  }
}
